import React from 'react';
import { BarChart3 } from 'lucide-react';

const pctColor = (pct: number) => pct > 50 ? 'var(--accent-red)' : pct > 30 ? 'var(--accent-amber)' : 'var(--accent-green)';

const ExcelBadge: React.FC = () => (
  <span style={{
    marginLeft: 8, fontSize: 9.5, padding: '2px 7px', borderRadius: 8, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 0.4, background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)',
  }}>Excel</span>
);

interface PLReportCardProps {
  data: any;
  className?: string;
}

/** Renders the same receipt-style P&L card + summary table + per-order section used by the
 * main P&L page. Shared so every P&L-shaped view (main, Excel Check, etc.) looks identical. */
const PLReportCard: React.FC<PLReportCardProps> = ({ data, className }) => {
  return (
    <div className={className}>
      {/* Main P&L Card */}
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', overflow: 'hidden',
      }}>
        {/* REVENUE */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 4 }}>
                Revenue (Ex-GST)
              </div>
              <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{data.paid_orders} orders · GST: ₹{data.gst_amount?.toLocaleString('en-IN')}</div>
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--accent-green)', letterSpacing: -0.5 }}>
              ₹{(data.revenue_exclusive ?? data.revenue).toLocaleString('en-IN')}
            </div>
          </div>
        </div>

        {/* COGS Section */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-amber)', textTransform: 'uppercase', letterSpacing: 0.8 }}>
              COGS (Direct Expenses)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {data.cogs.percentage}%
              </span>
              <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent-red)' }}>−₹{data.cogs.total.toLocaleString('en-IN')}</span>
            </div>
          </div>
          {Object.entries(data.cogs.breakdown || {}).map(([cat, info]: any) => (
            <div key={cat}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0 7px 16px' }}>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                  {cat}
                  {info.source === 'excel' && <ExcelBadge />}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 40, textAlign: 'right' }}>{info.percentage}%</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', minWidth: 80, textAlign: 'right' }}>₹{info.amount.toLocaleString('en-IN')}</span>
                </div>
              </div>
              {info.items && info.items.length > 0 && (
                <div style={{ marginLeft: 32, marginBottom: 4, borderLeft: '2px solid var(--border)', paddingLeft: 12 }}>
                  {info.items.map((item: any) => (
                    <div key={item.article_type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
                      <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{item.article_type}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {item.qty != null && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{item.qty} pcs</span>}
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)' }}>₹{item.amount.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  ))}
                  {info.items.some((i: any) => i.qty != null) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', marginTop: 2, borderTop: '1px solid var(--border)' }}>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Total</span>
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 700 }}>
                        {info.items.reduce((s: number, i: any) => s + (i.qty || 0), 0)} pcs
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {Object.keys(data.cogs.breakdown || {}).length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 0 4px 16px' }}>No COGS recorded</div>
          )}
        </div>

        {/* GROSS PROFIT */}
        <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Gross Profit</div>
              <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Revenue − COGS</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: data.gross_profit >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', letterSpacing: -0.3 }}>
                {data.gross_profit < 0 ? '−' : ''}₹{Math.abs(data.gross_profit).toLocaleString('en-IN')}
              </div>
              <div style={{ fontSize: 11, color: pctColor(100 - data.gross_margin), fontWeight: 600 }}>{data.gross_margin}% margin</div>
            </div>
          </div>
        </div>

        {/* PRINTS */}
        {data.prints && data.prints.total > 0 && (
          <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: Object.keys(data.prints.breakdown).length > 0 ? 8 : 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-amber)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Prints</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{data.prints.percentage}%</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent-red)' }}>−₹{data.prints.total.toLocaleString('en-IN')}</span>
              </div>
            </div>
            {Object.entries(data.prints.breakdown || {}).map(([cat, info]: any) => (
              <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0 5px 16px' }}>
                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{cat}</span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>₹{info.amount.toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
        )}

        {/* SHIPPING + GATEWAY */}
        {(['shipping', 'gateway'] as const).map(key => {
          const sec = data[key];
          if (!sec || sec.total === 0) return null;
          const labels: Record<string, string> = { shipping: 'Shipping', gateway: 'Gateway Fees' };
          const colors: Record<string, string> = { shipping: 'var(--accent-green)', gateway: 'var(--accent-purple)' };
          return (
            <div key={key} style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: Object.keys(sec.breakdown).length > 0 ? 8 : 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: colors[key], textTransform: 'uppercase', letterSpacing: 0.8 }}>{labels[key]}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{sec.percentage}%</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent-red)' }}>−₹{sec.total.toLocaleString('en-IN')}</span>
                </div>
              </div>
              {Object.entries(sec.breakdown || {}).map(([cat, info]: any) => (
                <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0 5px 16px' }}>
                  <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{cat}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>₹{info.amount.toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
          );
        })}

        {/* CONTRIBUTION MARGIN */}
        <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Contribution Margin</div>
              <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Revenue − COGS − Prints − Shipping − Gateway</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: data.contribution_margin >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', letterSpacing: -0.3 }}>
                {data.contribution_margin < 0 ? '−' : ''}₹{Math.abs(data.contribution_margin).toLocaleString('en-IN')}
              </div>
              <div style={{ fontSize: 11, color: pctColor(100 - data.contribution_margin_pct), fontWeight: 600 }}>{data.contribution_margin_pct}% margin</div>
            </div>
          </div>
        </div>

        {/* MARKETING */}
        {data.marketing && data.marketing.total > 0 && (
          <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#f97316', textTransform: 'uppercase', letterSpacing: 0.8 }}>Marketing</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{data.marketing.percentage}%</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent-red)' }}>−₹{data.marketing.total.toLocaleString('en-IN')}</span>
              </div>
            </div>
            {Object.entries(data.marketing.breakdown || {}).map(([cat, info]: any) => (
              <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0 5px 16px' }}>
                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{cat}</span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>₹{info.amount.toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
        )}

        {/* MARKETING CONTRIBUTION */}
        {data.marketing && data.marketing.total > 0 && (
          <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Marketing Contribution</div>
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Contribution Margin − Marketing</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: data.marketing_contribution >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', letterSpacing: -0.3 }}>
                  {data.marketing_contribution < 0 ? '−' : ''}₹{Math.abs(data.marketing_contribution).toLocaleString('en-IN')}
                </div>
                <div style={{ fontSize: 11, color: pctColor(100 - data.marketing_contribution_pct), fontWeight: 600 }}>{data.marketing_contribution_pct}% margin</div>
              </div>
            </div>
          </div>
        )}

        {/* FIXED EXPENSES */}
        {data.fixed && data.fixed.total > 0 && (
          <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-blue)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Fixed Expenses</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{data.fixed.percentage}%</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent-red)' }}>−₹{data.fixed.total.toLocaleString('en-IN')}</span>
              </div>
            </div>
            {Object.entries(data.fixed.breakdown || {}).map(([cat, info]: any) => (
              <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0 5px 16px' }}>
                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{cat}</span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>₹{info.amount.toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
        )}

        {/* NET PROFIT */}
        <div style={{ padding: '20px 24px', background: data.net_profit >= 0 ? 'linear-gradient(135deg, rgba(45,212,160,0.08), rgba(45,212,160,0.03))' : 'linear-gradient(135deg, rgba(239,90,111,0.08), rgba(239,90,111,0.03))' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>Net Profit</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Marketing Contribution − Fixed</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.8, color: data.net_profit >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                {data.net_profit < 0 ? '−' : ''}₹{Math.abs(data.net_profit).toLocaleString('en-IN')}
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2, color: data.net_margin >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                {data.net_margin}% net margin
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Table */}
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', padding: 24, marginTop: 16,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <BarChart3 size={16} color="var(--accent-blue)" /> Summary
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              {['Particular', 'Amount (₹)', '% of Revenue'].map(h => (
                <th key={h} style={{ padding: '8px 0', textAlign: h === 'Particular' ? 'left' : 'right', fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              { label: 'Revenue (ex-GST)', amount: data.revenue_exclusive ?? data.revenue, pct: 100, color: 'var(--accent-green)', bold: true },
              { label: 'COGS', amount: -data.cogs.total, pct: -data.cogs.percentage, color: 'var(--accent-red)', bold: false },
              { label: 'Gross Profit', amount: data.gross_profit, pct: data.gross_margin, color: data.gross_profit >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', bold: true },
              ...(data.prints?.total > 0 ? [{ label: 'Prints', amount: -(data.prints?.total || 0), pct: -(data.prints?.percentage || 0), color: 'var(--accent-red)', bold: false }] : []),
              { label: 'Shipping', amount: -(data.shipping?.total || 0), pct: -(data.shipping?.percentage || 0), color: 'var(--accent-red)', bold: false },
              { label: 'Gateway Fees', amount: -(data.gateway?.total || 0), pct: -(data.gateway?.percentage || 0), color: 'var(--accent-red)', bold: false },
              { label: 'Contribution Margin', amount: data.contribution_margin, pct: data.contribution_margin_pct, color: data.contribution_margin >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', bold: true },
              { label: 'Marketing', amount: -(data.marketing?.total || 0), pct: -(data.marketing?.percentage || 0), color: 'var(--accent-red)', bold: false },
              { label: 'Mktg Contribution', amount: data.marketing_contribution, pct: data.marketing_contribution_pct, color: data.marketing_contribution >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', bold: true },
              { label: 'Fixed Expenses', amount: -(data.fixed?.total || 0), pct: -(data.fixed?.percentage || 0), color: 'var(--accent-red)', bold: false },
              { label: 'Net Profit', amount: data.net_profit, pct: data.net_margin, color: data.net_profit >= 0 ? 'var(--accent-green)' : 'var(--accent-red)', bold: true },
            ].map((row, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '10px 0', fontSize: 13, fontWeight: row.bold ? 700 : 400, color: row.bold ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{row.label}</td>
                <td style={{ padding: '10px 0', textAlign: 'right', fontSize: 14, fontWeight: row.bold ? 700 : 500, color: row.color }}>
                  {row.amount < 0 ? '−' : ''}₹{Math.abs(row.amount).toLocaleString('en-IN')}
                </td>
                <td style={{ padding: '10px 0', textAlign: 'right', fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>{Math.abs(row.pct)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PER ORDER */}
      {data.per_order && Object.keys(data.per_order).length > 0 && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 24, marginTop: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>Per Order — Ex-GST ({data.fulfilled_orders} fulfilled)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {[
              { label: 'Revenue', value: data.per_order.revenue, color: 'var(--accent-green)' },
              { label: 'COGS', value: data.per_order.cogs, color: 'var(--accent-amber)' },
              { label: 'Prints', value: data.per_order.prints || 0, color: 'var(--accent-amber)' },
              { label: 'Shipping', value: data.per_order.shipping, color: 'var(--accent-green)' },
              { label: 'Contribution', value: data.per_order.contribution, color: data.per_order.contribution >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' },
              { label: 'Marketing', value: data.per_order.marketing, color: '#f97316' },
              { label: 'Mktg Contribution', value: data.per_order.marketing_contribution, color: data.per_order.marketing_contribution >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', padding: '12px 14px' }}>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4, fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color }}>{value < 0 ? '−' : ''}₹{Math.abs(value).toLocaleString('en-IN')}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PLReportCard;
